import { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { FunctionComponent } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimeValidationError } from '@mui/x-date-pickers';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import styles from './timePicker.module.css';
import { TimePickerProps } from './timePicker.types';

const bindTimeToDate = (
  time: Dayjs | undefined,
  refDate: Dayjs | undefined,
) => {
  if (!time || !time.isValid()) return undefined;
  if (refDate && refDate.isValid()) {
    return refDate
      .hour(time.hour())
      .minute(time.minute())
      .second(time.second() || 0)
      .millisecond(0);
  }
  return time;
};

const TimePickerV3: FunctionComponent<TimePickerProps> = ({
  value,
  defaultTime,
  date,
  isDateToday,
  disabled,
  ampm = true,
  format,
  minTime,
  maxTime,
  onChange,
  onValidityChange,
}) => {
  const [open, setOpen] = useState(false);
  const [fieldError, setFieldError] = useState<TimeValidationError | null>(
    null,
  );

  const computedDefaultValue = useMemo<Dayjs | undefined>(() => {
    if (!defaultTime) return undefined;
    const timeOnly =
      typeof defaultTime === 'string' ? dayjs(defaultTime) : defaultTime;
    if (!timeOnly || !timeOnly.isValid()) return undefined;
    return bindTimeToDate(timeOnly, date) ?? timeOnly;
  }, [defaultTime, date]);

  const computedMinTime = useMemo<Dayjs | undefined>(() => {
    return bindTimeToDate(minTime, date) ?? minTime;
  }, [minTime, date]);

  const computedMaxTime = useMemo<Dayjs | undefined>(() => {
    return bindTimeToDate(maxTime, date) ?? maxTime;
  }, [maxTime, date]);

  // Emit validity to parent whenever error changes
  useEffect(() => {
    onValidityChange?.(fieldError == null);
  }, [fieldError, onValidityChange]);

  const handleChange = (newValue: Dayjs | null) => {
    onChange?.(newValue);
  };

  const handleError = (reason: TimeValidationError, value: Dayjs | null) => {
    // reason can be:
    // 'invalidDate' | 'minutesStep' | 'minTime' | 'maxTime' | 'shouldDisableTime' | 'disableFuture' | 'disablePast' | null
    setFieldError(reason ?? null);
  };

  const helperText =
    fieldError === 'maxTime'
      ? 'Please select a time not later than the allowed maximum.'
      : fieldError === 'minTime'
        ? 'Please select a time not earlier than the allowed minimum.'
        : fieldError === 'invalidDate'
          ? 'Invalid time.'
          : fieldError === 'minutesStep'
            ? 'Please select a time matching the minutes step.'
            : fieldError === 'shouldDisableTime-hours'
              ? 'This time is disabled.'
              : fieldError === 'disableFuture'
                ? 'Future times are disabled for the selected date.'
                : fieldError === 'disablePast'
                  ? 'Past times are disabled for the selected date.'
                  : undefined;

  return (
    <div className={styles.selectedTime}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={handleChange}
          onError={handleError}
          value={value}
          defaultValue={value ? undefined : (computedDefaultValue ?? dayjs())}
          // Only set referenceDate if you actually have a date to anchor to
          referenceDate={date && date.isValid() ? date : undefined}
          // Use computed bounds (anchored to the same date)
          minTime={computedMinTime}
          maxTime={computedMaxTime}
          viewRenderers={{
            hours: renderTimeViewClock,
            minutes: renderTimeViewClock,
            seconds: renderTimeViewClock,
          }}
          disableFuture={isDateToday}
          disabled={disabled}
          ampm={ampm}
          format={format}
          slotProps={{
            popper: { className: 'timepicker-popper' },
            textField: {
              required: true,
              variant: 'outlined',
              error: !!fieldError,
              helperText,
              className: `${styles.textFieldRoot} ${open ? styles.open : ''}`,
            },
          }}
        />
      </LocalizationProvider>
    </div>
  );
};

export default TimePickerV3;
